/**
 * RiskTreatmentMatrixCard — 4×4 heat map: risk severity (rows,
 * Critical→Low) × treatment decision (columns, Reduce/Share/Retain/
 * Avoid). Cell ink is a one-hue sequential scale (counts are magnitude,
 * so sequential — never a categorical rainbow), stepped from the
 * --chart-1 hue; every cell carries its count as a direct label, and a
 * title tooltip spells out the combination.
 *
 * Replaces the separate Risk Distribution and Treatment Decisions
 * cards. Pure presentation; consumes `data.riskTreatmentMatrix`.
 */
import React from 'react';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
} from '../../../../components/ui/Empty';
import type { AggregatedOverviewData, Severity, TreatmentKey } from '../../aggregate/types';
// Reuse the Chart primitive's tooltip chrome so the heat map's hover
// reads identically to the pie/bar tooltips.
import chartStyles from '../../../../components/ui/Chart/Chart.module.css';
import styles from './RiskTreatmentMatrixCard.module.css';

interface RiskTreatmentMatrixCardProps {
  data: AggregatedOverviewData;
}

const SEVERITY_ROWS: { key: Severity; label: string }[] = [
  { key: 'critical', label: 'Critical' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
];

const TREATMENT_COLS: { key: TreatmentKey; label: string }[] = [
  { key: 'reduce', label: 'Reduce' },
  { key: 'share', label: 'Share' },
  { key: 'retain', label: 'Retain' },
  { key: 'avoid', label: 'Avoid' },
];

/* Sequential intensity step for a cell: 0 = empty track, then four
   quartile steps of the max. Returns a class-name suffix. */
const intensityStep = (count: number, max: number): 0 | 1 | 2 | 3 | 4 => {
  if (count <= 0 || max <= 0) return 0;
  const ratio = count / max;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
};

export default function RiskTreatmentMatrixCard({ data }: RiskTreatmentMatrixCardProps) {
  const matrix = data.riskTreatmentMatrix;
  const grandTotal = matrix.total + matrix.untreated;

  if (grandTotal === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <TypographyH2 className={styles.cardTitle}>Risk Distribution × Treatment</TypographyH2>
        </div>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No risks assessed yet</EmptyTitle>
            <EmptyDescription>Populates after Step 6.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>Risk Distribution × Treatment</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>
          {matrix.total} risk{matrix.total === 1 ? '' : 's'}
          {matrix.untreated > 0 ? ` · ${matrix.untreated} untreated` : ''}
        </TypographyMuted>
      </div>

      <div className={styles.matrix} role="grid" aria-label="Risks by severity and treatment decision">
        {/* Corner + column headers */}
        <span className={styles.corner} aria-hidden="true" />
        {TREATMENT_COLS.map((col) => (
          <TypographySmall key={col.key} className={styles.colHeader}>
            {col.label}
          </TypographySmall>
        ))}

        {/* One row per severity: header + 4 cells */}
        {SEVERITY_ROWS.map((row) => (
          <React.Fragment key={row.key}>
            <TypographySmall className={styles.rowHeader}>{row.label}</TypographySmall>
            {TREATMENT_COLS.map((col) => {
              const count = matrix.cells[row.key][col.key];
              const step = intensityStep(count, matrix.max);
              return (
                <div key={col.key} role="gridcell" className={styles.cellWrap}>
                  <div
                    className={`${styles.cell} ${styles[`cellStep${step}`]}`}
                    aria-label={`${row.label} severity with ${col.label} treatment: ${count}`}
                  >
                    {count}
                  </div>
                  <div
                    className={`${chartStyles.tooltip} ${styles.cellTooltip}`}
                    role="tooltip"
                    aria-hidden="true"
                  >
                    <div className={`${chartStyles.tooltipItem} ${chartStyles.tooltipItemCentered}`}>
                      <div
                        className={`${chartStyles.indicator} ${chartStyles.indicatorDot}`}
                        style={
                          {
                            '--color-bg': 'var(--chart-1)',
                            '--color-border': 'var(--chart-1)',
                          } as React.CSSProperties
                        }
                      />
                      <div
                        className={`${chartStyles.tooltipValueRow} ${chartStyles.tooltipValueRowCentered}`}
                      >
                        <span className={chartStyles.tooltipName}>
                          {row.label} × {col.label}
                        </span>
                        <span className={chartStyles.tooltipValue}>{count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
